import React, { useState, useRef, useEffect, useMemo } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiSearch, FiBell, FiActivity, FiAlertTriangle, FiAward, 
  FiInfo, FiCheck, FiMenu, FiInbox, FiRefreshCw, FiZap,
  FiShield, FiCpu, FiClock
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const TopBar = ({ xp, tickets = [], currentTime, onMenuOpen, user }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate a deep system scan
    setTimeout(() => {
      setIsRefreshing(false);
    }, 800);
  };

  // Advanced Real-Time Notification Logic
  const notifications = useMemo(() => {
    const list = [];
    const openTickets = tickets.filter(t => t.status === 'open');
    const resolvedTickets = tickets.filter(t => t.status === 'resolved');

    // 1. RECENT ARRIVALS (Tickets opened in the last 15 minutes)
    openTickets.forEach(t => {
      const ageMs = currentTime - t.openedAt;
      if (ageMs < 900000 && ageMs > 0) { // 15 mins
        list.push({
          id: `new-${t.id}`,
          type: 'new',
          title: 'New Incident Assigned',
          message: `${t.id} (${t.category}) has entered your queue.`,
          time: 'NEW',
          icon: FiInbox,
          color: 'text-blue-500',
          bg: 'bg-blue-50'
        });
      }
    });

    // 2. SLA MONITORING (Critical & Warnings)
    openTickets.forEach(t => {
      const elapsedMs = currentTime - t.openedAt;
      const elapsedMinutes = Math.floor(elapsedMs / 60000);
      const remaining = t.slaMinutes - elapsedMinutes;

      if (remaining <= 0) {
        list.push({
          id: `breach-${t.id}`,
          type: 'critical',
          title: 'SLA BREACHED',
          message: `Incident ${t.id} is now out of compliance. Resolve immediately.`,
          time: 'CRITICAL',
          icon: FiAlertTriangle,
          color: 'text-red-600',
          bg: 'bg-red-50'
        });
      } else if (remaining <= 15) {
        list.push({
          id: `sla-${t.id}`,
          type: 'warning',
          title: 'SLA Warning',
          message: `${t.id} will breach in ${remaining} minutes.`,
          time: `${remaining}m`,
          icon: FiClock,
          color: 'text-orange-500',
          bg: 'bg-orange-50'
        });
      }
    });

    // 3. CLEARANCE & ACCESS NOTIFICATIONS
    if (user?.tier2Access) {
      list.push({
        id: 'access-t2',
        type: 'clearance',
        title: 'Senior Access Granted',
        message: 'Your account has been authorized for the Tier 2 Senior Queue.',
        time: 'System',
        icon: FiShield,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50'
      });
    }

    // 4. QUEUE VOLUME ALERTS
    if (openTickets.length > 10) {
      list.push({
        id: 'vol-high',
        type: 'status',
        title: 'High Queue Volume',
        message: `There are currently ${openTickets.length} open tickets. Consider prioritizing Critical items.`,
        time: 'Alert',
        icon: FiActivity,
        color: 'text-amber-600',
        bg: 'bg-amber-50'
      });
    }

    // 5. XP MILESTONES
    const milestones = [
      { thresh: 150, title: 'First Resolution', msg: 'You have earned your first 150 XP!' },
      { thresh: 1000, title: 'Expert Status', msg: '1,000 XP Milestone reached. Performance is optimal.' },
      { thresh: 5000, title: 'Elite Analyst', msg: '5,000 XP achieved. You are in the top 1%.' }
    ];

    milestones.reverse().forEach(m => {
      if (xp >= m.thresh) {
        list.push({
          id: `xp-${m.thresh}`,
          type: 'achievement',
          title: m.title,
          message: m.msg,
          time: 'Earned',
          icon: FiAward,
          color: 'text-emerald-500',
          bg: 'bg-emerald-50'
        });
      }
    });

    // Default Fallback
    if (list.length === 0) {
      list.push({
        id: 'welcome',
        type: 'info',
        title: 'System Nominal',
        message: 'Operational status green. No immediate actions required.',
        time: 'Now',
        icon: FiCheck,
        color: 'text-slate-400',
        bg: 'bg-slate-50'
      });
    }

    return list;
  }, [xp, tickets, currentTime, user?.tier2Access]);

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 relative z-40">
      <div className="flex items-center flex-1 space-x-4">
        <button onClick={onMenuOpen} className="p-2 md:hidden text-slate-500 hover:bg-slate-100 rounded-lg">
          <SafeIcon icon={FiMenu} className="text-xl" />
        </button>
        <div className="relative w-full max-w-xs md:max-w-md">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search worklist..." 
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
          />
        </div>
      </div>

      <div className="flex items-center space-x-3 md:space-x-6">
        <div className="flex items-center space-x-2 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100 shrink-0 shadow-sm">
          <SafeIcon icon={FiZap} className="text-emerald-500" />
          <span className="text-xs md:text-sm font-black text-emerald-700">{xp} <span className="hidden sm:inline opacity-60">XP</span></span>
        </div>

        <div className="relative" ref={dropdownRef}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            className={`relative p-2.5 transition-all rounded-xl ${showNotifications ? 'bg-slate-900 text-white shadow-xl' : 'text-slate-400 hover:text-slate-800 hover:bg-slate-50 border border-transparent hover:border-slate-200'}`}
          >
            <SafeIcon icon={FiBell} className="text-xl" />
            {(notifications.length > 0 && notifications[0].id !== 'welcome') && (
              <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white ring-2 ring-red-500/20"></span>
            )}
          </button>

          <AnimatePresence>
            {showNotifications && (
              <motion.div 
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute right-0 mt-3 w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-[2rem] shadow-3xl border border-slate-200 overflow-hidden"
              >
                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white">
                      <SafeIcon icon={FiActivity} className="text-sm" />
                    </div>
                    <div>
                      <h3 className="font-black text-slate-800 text-sm uppercase tracking-tight">System Events</h3>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-0.5">
                        {notifications.length} Active Notifications
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="p-2 hover:bg-white hover:shadow-md rounded-xl text-slate-400 hover:text-blue-600 transition-all flex items-center space-x-1.5 border border-transparent hover:border-slate-100"
                  >
                    <SafeIcon icon={FiRefreshCw} className={`text-xs ${isRefreshing ? 'animate-spin text-blue-600' : ''}`} />
                    <span className="text-[9px] font-black uppercase tracking-widest">Rescan</span>
                  </button>
                </div>

                <div className="max-h-[450px] overflow-y-auto hide-scrollbar">
                  {notifications.map(notif => (
                    <div key={notif.id} className="p-5 hover:bg-slate-50 border-b border-slate-50 transition-colors group">
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 rounded-2xl ${notif.bg} shadow-sm border border-black/5 shrink-0 group-hover:scale-110 transition-transform`}>
                          <SafeIcon icon={notif.icon} className={`${notif.color} text-lg`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="text-xs font-black text-slate-900 truncate uppercase tracking-tight">{notif.title}</h4>
                            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-widest ml-2 shrink-0 ${notif.type === 'critical' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-400'}`}>
                              {notif.time}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 font-medium leading-relaxed">{notif.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="p-4 bg-slate-50 text-center border-t border-slate-100">
                  <p className="text-[9px] text-slate-400 font-black uppercase tracking-[0.2em]">
                    Kernel 4.2.0-STABLE | All Systems Nominal
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default TopBar;